import {
  FotoCredito,
  SiteLayout,
  PhotoHero,
} from "@/components/site/SiteLayout";
import { Eye, Navigation, ShieldCheck, Wallet } from "lucide-react";
import { buildParquesListJsonLd } from "@/components/parques/parqueJsonLd";
import { FeatureCard } from "@/components/site/Stickers";
import { getParqueBySlug, parquePath, parques, type Parque } from "@/content/parques";
import { formatCOP } from "@/constants";
import tayronaFoto from "@/assets/tayrona-hero.avif";

/**
 * /parques — hub for the park guides (DESIGN.md).
 *
 * Photo hero, then one drenched panel per park in its own color (the
 * Mindtrip "Learns / Acts / Adapts" panels), then the closing Tayrona panel.
 */

const lowestFee = (valores: number[]): number | undefined =>
  valores.filter((v) => v > 0).sort((a, b) => a - b)[0];

/** Throws at build time if a sticker points at data that no longer exists. */
function parque(slug: string): Parque {
  const p = getParqueBySlug(slug);
  if (!p) throw new Error(`Sticker: unknown park "${slug}"`);
  return p;
}

function tarifa(p: Parque, grupo: string): string {
  const t = p.tarifas.find((x) => x.grupo === grupo);
  if (!t) throw new Error(`Sticker: no fee "${grupo}" for ${p.slug}`);
  return t.valor === 0 ? "No paga" : formatCOP(t.valor);
}

const thumb = (p: Parque) => p.fotos.detalle.src;

/**
 * "Planea tu visita" feature row. Every sticker is a fact from parques.ts:
 * fees and status are read live; the travel and rule facts quote the
 * park's `accesos` / `reglas` / `seguro` fields (keep them in sync).
 */
function planCards() {
  const cano = parque("cano-cristales");
  const gorgona = parque("isla-gorgona");
  const cocuy = parque("nevado-del-cocuy");
  const rosario = parque("islas-del-rosario");
  const chingaza = parque("chingaza");
  return [
    {
      icon: Eye,
      title: "Revisa.",
      text: "Mira si el parque está abierto y qué cierres tiene antes de comprar nada.",
      stickers: [
        { img: thumb(cano), label: cano.atractivoPrincipal, fact: "Abierto hasta diciembre", tone: "good" as const, tilt: -2 },
        { img: thumb(gorgona), label: gorgona.atractivoPrincipal, fact: "Solo pasadía", tilt: 3 },
      ],
    },
    {
      icon: Wallet,
      title: "Calcula.",
      text: "Elige tu caso y mira cuánto cuesta entrar en 2026, con la resolución oficial.",
      stickers: [
        { img: thumb(cocuy), label: `${cocuy.atractivoPrincipal}, adulto`, fact: tarifa(cocuy, "Colombiano o residente, de 25 a 62 años"), tilt: 2 },
        { img: thumb(cocuy), label: "Mayor de 62 años", fact: tarifa(cocuy, "Mayor de 62 años"), tone: "good" as const, tilt: -3 },
        { img: thumb(chingaza), label: `${chingaza.atractivoPrincipal}, adulto`, fact: tarifa(chingaza, "Colombiano o residente, mayor de 25 años"), tilt: 1 },
      ],
    },
    {
      icon: Navigation,
      title: "Llega.",
      text: "Rutas oficiales desde la ciudad más cercana, con tiempos reales.",
      stickers: [
        { img: thumb(gorgona), label: "Cali a Gorgona", fact: "Vuelo 40 min + lancha 1 h 30", tilt: -2 },
        { img: thumb(cocuy), label: "Bogotá a El Cocuy", fact: "Bus directo diario", tilt: 3 },
      ],
    },
    {
      icon: ShieldCheck,
      title: "Prepárate.",
      text: "Seguro, guía y reglas, para que no te devuelvan en la entrada.",
      stickers: [
        { img: thumb(rosario), label: rosario.atractivoPrincipal, fact: "Seguro obligatorio", tone: "alert" as const, tilt: 2 },
        { img: thumb(cano), label: cano.atractivoPrincipal, fact: "Guía: 1 por 9 personas", tilt: -3 },
      ],
    },
  ];
}

const Parques = () => {
  const portada = parques[0].fotos.portada;

  return (
    <SiteLayout current="parques">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildParquesListJsonLd(parques)) }}
      />

      <PhotoHero
        foto={portada}
        credit={<FotoCredito foto={portada} />}
      />

      <section className="pq-wrap pq-intro">
        <nav aria-label="Ruta de navegación" className="pq-crumbs pq-rise">
          <a href="/">Inicio</a>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Parques</span>
        </nav>
        <h1 className="pq-display pq-rise pq-rise-2">
          Parques naturales de Colombia.
        </h1>
        <p className="pq-lead pq-rise pq-rise-3">
          Cinco parques que valen el viaje, con lo que necesitas para ir: cuánto
          cuesta entrar, cómo llegar, horarios y reglas. Cada dato viene del
          sitio oficial de Parques Nacionales.
        </p>
      </section>

      <section id="lista" aria-label="Parques" className="pq-section">
        <div className="pq-wrap">
          {parques.map((parque) => {
            const desde = lowestFee(parque.tarifas.map((t) => t.valor));
            const abierto = parque.estado === "abierto";
            return (
              <article
                key={parque.slug}
                className={`pq-panel pq-tema-${parque.tema}`}
                aria-labelledby={`p-${parque.slug}`}
              >
                <div className="pq-panel-body">
                  <h2 id={`p-${parque.slug}`} className="pq-headline">
                    {parque.atractivoPrincipal}.
                  </h2>
                  <p className="pq-panel-text">{parque.intro}</p>
                  <p className="pq-panel-note">
                    {parque.nombreOficial} · {parque.departamentos.join(", ")}
                  </p>
                  <div>
                    <a href={parquePath(parque)} className="pq-btn pq-btn-light">
                      Ver la guía
                      <span className="sr-only"> de {parque.atractivoPrincipal}</span>
                    </a>
                  </div>
                </div>
                <figure className="pq-panel-figure">
                  <img
                    src={parque.fotos.detalle.src}
                    alt={parque.fotos.detalle.alt}
                    width={parque.fotos.detalle.width}
                    height={parque.fotos.detalle.height}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="pq-panel-chips pq-float">
                    <span className="pq-chip pq-chip-frost">
                      <span
                        className={`pq-dot${abierto ? "" : " pq-dot-alert"}`}
                        aria-hidden="true"
                      />
                      {abierto ? "Abierto" : "Con restricciones"}
                    </span>
                    {desde ? (
                      <span className="pq-chip pq-chip-frost">Desde {formatCOP(desde)}</span>
                    ) : null}
                    {parque.ciudadCercana ? (
                      <span className="pq-chip pq-chip-frost">{parque.ciudadCercana}</span>
                    ) : null}
                  </div>
                  <figcaption className="pq-figcredit">
                    <FotoCredito foto={parque.fotos.detalle} />
                  </figcaption>
                </figure>
              </article>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="plan-h" className="pq-section pq-mist">
        <div className="pq-wrap" style={{ display: "grid", gap: 48 }}>
          <div style={{ display: "grid", gap: 16 }}>
            <h2 id="plan-h" className="pq-headline">Planea tu visita.</h2>
            <p className="pq-lead">
              Lo que revisa un local antes de ir a un parque nacional, con los
              datos oficiales de cada guía.
            </p>
          </div>
          <div className="pq-features">
            {planCards().map((card) => (
              <FeatureCard key={card.title} icon={card.icon} title={card.title} stickers={card.stickers}>
                {card.text}
              </FeatureCard>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="cierre-h" className="pq-wrap" style={{ paddingBlock: "var(--pq-section)" }}>
        <div className="pq-closing">
          <img
            src={tayronaFoto}
            alt="Vista panorámica del Parque Nacional Natural Tayrona, Colombia"
            loading="lazy"
            decoding="async"
          />
          <div className="pq-closing-body">
            <h2 id="cierre-h" className="pq-headline">¿Y el Tayrona?</h2>
            <p>
              Si buscas acampar frente al mar, comparamos los campings del
              Parque Tayrona con precios y fechas de cierre.
            </p>
            <a href="/#campsites" className="pq-btn pq-btn-light">
              Ver campings del Tayrona
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Parques;
